import { useState, useEffect } from 'react';
import { TextField, Tooltip } from '@mui/material';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import { useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import BackgroundLetterAvatarsLogo from '../../components/AvatarLogo';
import { useToast } from '../../components/Toast';
import { useCurrentUser } from '../../components/CurrentUser';
import ProfilePhotoDialog from './ProfilePhotoDialog';
import AccountSettings from './AccountSettings';
import './profile.scss';

/* Each editable row, so the markup below stays one loop rather than four
   near-identical blocks. */
const ABOUT_FIELDS = [
    { key: 'role', label: 'Role', icon: WorkOutlineRoundedIcon, fallback: 'Your job title or role' },
    { key: 'department', label: 'Department', icon: AccountTreeRoundedIcon, fallback: 'Your department' },
    {
        key: 'organization',
        label: 'Organization',
        icon: ApartmentRoundedIcon,
        fallback: 'No organization',
        // Stored as an ObjectId ref, so it is not a free-text field a member
        // can retype — it is owned by the organisation record.
        readOnly: true,
        hint: 'Managed by your organization — contact an admin to change it',
    },
    { key: 'location', label: 'Location', icon: PlaceOutlinedIcon, fallback: 'Your location' },
];

const Profile = () => {
    const toast = useToast();
    const { user, updateUser } = useCurrentUser();

    const [editField, setEditField] = useState(null);
    const [draft, setDraft] = useState('');
    const [photoOpen, setPhotoOpen] = useState(false);
    const [details, setDetails] = useState({
        name: '',
        role: '',
        department: '',
        location: '',
    });

    // The header's "Settings" entry links here with #settings.
    const { hash } = useLocation();
    useEffect(() => {
        if (hash === '#settings') {
            document.getElementById('settings')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }
    }, [hash]);

    const { Projects } = useSelector((state) => state.ProjamAppSlice);
    const projectsData = Projects.allProjectsList;
    const [userProject, setUserProject] = useState();

    useEffect(() => {
        if (projectsData) {
            setUserProject(projectsData?.data);
        }
    }, [projectsData]);

    // The provider owns the fetch; this page just mirrors it into editable state.
    useEffect(() => {
        if (!user) return;
        setDetails({
            name: user.name || '',
            role: user.role || '',
            department: user.department || '',
            location: user.location || '',
        });
    }, [user]);

    const beginEdit = (field) => {
        setDraft(details[field] || '');
        setEditField(field);
    };

    const cancelEdit = () => {
        setEditField(null);
        setDraft('');
    };

    const saveField = async (field) => {
        const value = draft.trim();
        if (value === (details[field] || '')) {
            cancelEdit();
            return;
        }
        try {
            await updateUser({ [field]: value });
            setDetails((prev) => ({ ...prev, [field]: value }));
            toast.success('Profile updated');
            cancelEdit();
        } catch (error) {
            console.error('Error updating user data', error);
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                'Could not save that change. Please try again.'
            );
        }
    };

    const savePhoto = async (dataUrl) => {
        // Same profile endpoint as every other field — the avatar is just
        // another column on the user record.
        await updateUser({ profileImage: dataUrl || '' });
        toast.success(dataUrl ? 'Profile photo updated' : 'Profile photo removed');
    };

    /** Enter commits, Escape abandons — expected of an inline editor. */
    const onEditorKeyDown = (event, field) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveField(field);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
        }
    };

    const editor = (field) => (
        <div className="pf-editor">
            <TextField
                className="pf-field"
                size="small"
                fullWidth
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => onEditorKeyDown(e, field)}
                autoFocus
            />
            <button type="button" className="pf-icon-btn is-confirm" onClick={() => saveField(field)} aria-label="Save">
                <CheckRoundedIcon />
            </button>
            <button type="button" className="pf-icon-btn is-cancel" onClick={cancelEdit} aria-label="Cancel">
                <CloseRoundedIcon />
            </button>
        </div>
    );

    const projectCount = userProject?.length || 0;

    return (
        <div className="pj-profile">
            <div className="pf-wrap">
                {/* ------------------------------------------------ identity */}
                <section className="pf-card pf-ident">
                    <div className="pf-ident-cover" />

                    <div className="pf-avatar">
                        <BackgroundLetterAvatarsLogo
                            LogoTitle={details.name || user?.name || ''}
                            isCustomised
                            RequiredFontSize="42px"
                            profileImage={user?.profileImage || null}
                        />
                        <button
                            type="button"
                            className="pf-avatar-edit"
                            onClick={() => setPhotoOpen(true)}
                            aria-label="Change profile photo"
                        >
                            <PhotoCameraRoundedIcon />
                        </button>
                    </div>

                    {editField === 'name' ? (
                        <div style={{ padding: '0 8px' }}>{editor('name')}</div>
                    ) : (
                        <div className="pf-name-row">
                            <h2 className="pf-name">{details.name || 'Your name'}</h2>
                            <span
                                className="pf-edit-icon"
                                role="button"
                                tabIndex={0}
                                onClick={() => beginEdit('name')}
                                onKeyDown={(e) => e.key === 'Enter' && beginEdit('name')}
                                aria-label="Edit name"
                            >
                                <EditRoundedIcon />
                            </span>
                        </div>
                    )}

                    <p className="pf-email">{user?.email}</p>

                    <span className="pf-chip">
                        <VerifiedUserRoundedIcon />
                        {details.role || 'Team member'}
                    </span>

                    <div className="pf-about">
                        <span className="pf-section-label">About</span>

                        {ABOUT_FIELDS.map(({ key, label, icon: Icon, fallback, readOnly, hint }) => {
                            // The organisation name is resolved server-side from the ref.
                            const value = readOnly ? user?.organizationName : details[key];

                            return (
                                <div className={`pf-row ${readOnly ? 'is-readonly' : ''}`} key={key}>
                                    <span className="pf-row-icon"><Icon /></span>
                                    <div className="pf-row-body">
                                        <span className="pf-row-label">{label}</span>

                                        {readOnly ? (
                                            <div className="pf-row-value is-static">
                                                {value || fallback}
                                                <Tooltip title={hint} placement="top">
                                                    <span className="pf-lock" aria-label={hint}>
                                                        <LockOutlinedIcon />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        ) : editField === key ? (
                                            editor(key)
                                        ) : (
                                            <div className="pf-row-value" onClick={() => beginEdit(key)}>
                                                {value || fallback}
                                                <span
                                                    className="pf-edit-icon"
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && beginEdit(key)}
                                                    aria-label={`Edit ${label.toLowerCase()}`}
                                                >
                                                    <EditRoundedIcon />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <div className="pf-main">
                    {/* ------------------------------------------------ projects */}
                    <section className="pf-card" style={{ '--pf-d': '.08s' }}>
                        <div className="pf-panel-head">
                            <h3 className="pf-panel-title">
                                <DashboardRoundedIcon />
                                Current projects
                            </h3>
                            <span className="pf-count">{projectCount}</span>
                        </div>

                        {projectCount ? (
                            <div className="pf-table-wrap">
                                <table className="pf-table">
                                    <thead>
                                        <tr>
                                            <th>Project name</th>
                                            <th>Key</th>
                                            <th>Created</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userProject.map((row, index) => (
                                            <tr key={row.key || index} style={{ '--pf-d': `${0.12 + index * 0.04}s` }}>
                                                <td className="pf-project-name">{row.title}</td>
                                                <td><span className="pf-key">{row.key}</span></td>
                                                <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`pf-status ${row.active === "true" ? 'is-active' : 'is-inactive'}`}>
                                                        {row.active === "true" ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="pf-empty">
                                <FolderOpenRoundedIcon />
                                <p>You are not on any projects yet.</p>
                            </div>
                        )}
                    </section>

                    {/* ------------------------------------------------ settings */}
                    <AccountSettings />
                </div>
            </div>

            <ProfilePhotoDialog
                open={photoOpen}
                onClose={() => setPhotoOpen(false)}
                name={details.name || user?.name || ''}
                currentImage={user?.profileImage || null}
                onSave={savePhoto}
            />
        </div>
    );
};

export default Profile;
